/**
 * Token account fetching and management
 * Handles retrieving SPL tokens from a wallet
 */

import { Connection, PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from "@solana/spl-token";
import { enrichTokensWithMetadata, TokenMetadata } from "./metadata";

/** Native SOL mint (wrapped SOL) - excluded from burn list */
const NATIVE_SOL_MINT = "So11111111111111111111111111111111111111112";

/** Known token program IDs for verification */
const TOKEN_PROGRAM_ID_STR = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
const TOKEN_2022_PROGRAM_ID_STR = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb";

/**
 * Represents a token account with balance and metadata
 */
export interface TokenAccountInfo {
  /** The token account public key */
  pubkey: PublicKey;
  /** The mint address */
  mint: PublicKey;
  /** Owner wallet address */
  owner: PublicKey;
  /** Raw token balance */
  amount: bigint;
  /** Token decimals */
  decimals: number;
  /** Token program (SPL Token or Token-2022) - verified from mint account */
  programId: PublicKey;
  /** Token symbol from metadata */
  symbol?: string;
  /** Token name from metadata */
  name?: string;
  /** Token logo URL from metadata */
  logoURI?: string;
  /** Whether this is a Token-2022 token */
  isToken2022: boolean;
}

/**
 * Verify the token program by checking the mint account's owner
 * This is the authoritative way to determine if a token is Token-2022 or legacy
 *
 * @param connection - Solana RPC connection
 * @param mintAddress - The mint public key
 * @returns The correct token program ID, or null if not a valid SPL token
 */
export async function verifyTokenProgram(
  connection: Connection,
  mintAddress: PublicKey
): Promise<{ programId: PublicKey; isToken2022: boolean } | null> {
  try {
    const accountInfo = await connection.getAccountInfo(mintAddress);

    if (!accountInfo) {
      return null;
    }

    const ownerStr = accountInfo.owner.toBase58();

    if (ownerStr === TOKEN_PROGRAM_ID_STR) {
      return { programId: TOKEN_PROGRAM_ID, isToken2022: false };
    }

    if (ownerStr === TOKEN_2022_PROGRAM_ID_STR) {
      return { programId: TOKEN_2022_PROGRAM_ID, isToken2022: true };
    }

    return null;
  } catch (error) {
    console.error(`[tokens] Error verifying token program:`, error);
    return null;
  }
}

/**
 * Fetch all SPL token accounts for a wallet
 * Includes both Token Program and Token-2022 tokens
 * Excludes native SOL and zero-balance accounts
 *
 * IMPORTANT: Token program is verified by checking mint account owner,
 * not by which query returned the token.
 *
 * @param connection - Solana RPC connection
 * @param owner - Wallet public key
 * @returns Array of token accounts with metadata
 */
export async function fetchTokenAccounts(
  connection: Connection,
  owner: PublicKey
): Promise<TokenAccountInfo[]> {
  // Collect raw token data first
  const rawTokens: Array<{
    pubkey: PublicKey;
    mintAddress: string;
    amount: bigint;
    decimals: number;
  }> = [];

  // Fetch from both Token Program and Token-2022
  // Note: We query both to find all tokens, but we'll verify the program later
  for (const programId of [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID]) {
    try {
      const accounts = await connection.getParsedTokenAccountsByOwner(owner, {
        programId,
      });

      for (const { pubkey, account } of accounts.value) {
        const parsed = account.data.parsed.info;
        const mintAddress = parsed.mint as string;

        // Skip native SOL (wrapped SOL)
        if (mintAddress === NATIVE_SOL_MINT) continue;

        // Skip zero balance accounts
        const amount = BigInt(parsed.tokenAmount.amount);
        if (amount === 0n) continue;

        // Check if we already have this token (avoid duplicates)
        if (!rawTokens.some((t) => t.mintAddress === mintAddress)) {
          rawTokens.push({
            pubkey,
            mintAddress,
            amount,
            decimals: parsed.tokenAmount.decimals,
          });
        }
      }
    } catch (error) {
      console.error(`[tokens] Error fetching accounts:`, error);
    }
  }

  if (rawTokens.length === 0) {
    return [];
  }

  // Verify token programs by checking mint owners (in parallel batches)
  const BATCH_SIZE = 10;
  const tokenAccounts: TokenAccountInfo[] = [];

  for (let i = 0; i < rawTokens.length; i += BATCH_SIZE) {
    const batch = rawTokens.slice(i, i + BATCH_SIZE);

    const verifyPromises = batch.map(async (token) => {
      const mint = new PublicKey(token.mintAddress);
      const programInfo = await verifyTokenProgram(connection, mint);

      if (!programInfo) {
        return null;
      }

      return {
        pubkey: token.pubkey,
        mint,
        owner,
        amount: token.amount,
        decimals: token.decimals,
        programId: programInfo.programId,
        isToken2022: programInfo.isToken2022,
      } as TokenAccountInfo;
    });

    const results = await Promise.all(verifyPromises);
    for (const result of results) {
      if (result) {
        tokenAccounts.push(result);
      }
    }
  }

  // Enrich with metadata (Jupiter + fallbacks)
  const mintAddresses = tokenAccounts.map((t) => t.mint.toBase58());
  const metadataMap = await enrichTokensWithMetadata(connection, mintAddresses);

  for (const token of tokenAccounts) {
    const metadata = metadataMap.get(token.mint.toBase58());
    if (metadata) {
      token.symbol = metadata.symbol;
      token.name = metadata.name;
      token.logoURI = metadata.logoURI;
    }
  }

  // Sort: tokens with metadata first, then by symbol
  tokenAccounts.sort((a, b) => {
    if (a.symbol && !b.symbol) return -1;
    if (!a.symbol && b.symbol) return 1;
    if (a.symbol && b.symbol) {
      return a.symbol.localeCompare(b.symbol);
    }
    return a.amount > b.amount ? -1 : 1;
  });

  return tokenAccounts;
}

/**
 * Get the SOL balance for a wallet
 * @param connection - Solana RPC connection
 * @param owner - Wallet public key
 * @returns Balance in lamports
 */
export async function getSolBalance(connection: Connection, owner: PublicKey): Promise<number> {
  try {
    return await connection.getBalance(owner);
  } catch (error) {
    console.error("[tokens] Error fetching SOL balance:", error);
    return 0;
  }
}

/**
 * Check if an account has enough SOL to pay transaction fees
 * @param connection - Solana RPC connection
 * @param owner - Wallet public key
 * @param requiredLamports - Minimum required lamports (default ~10000 for burn tx)
 * @returns true if sufficient balance
 */
export async function hasSufficientSol(
  connection: Connection,
  owner: PublicKey,
  requiredLamports: number = 10000
): Promise<boolean> {
  const balance = await getSolBalance(connection, owner);
  return balance >= requiredLamports;
}

/**
 * Represents an empty token account (zero balance) that can be closed for rent reclaim
 */
export interface EmptyTokenAccount {
  /** The token account public key */
  pubkey: PublicKey;
  /** The mint address */
  mint: PublicKey;
  /** Owner wallet address */
  owner: PublicKey;
  /** Token program (SPL Token or Token-2022) */
  programId: PublicKey;
  /** Whether this is a Token-2022 token (cannot be closed) */
  isToken2022: boolean;
  /** Token symbol from metadata */
  symbol?: string;
  /** Token name from metadata */
  name?: string;
  /** Token logo URL from metadata */
  logoURI?: string;
  /** Estimated rent refund in lamports */
  rentLamports: number;
}

/** Typical rent per token account in lamports */
const RENT_PER_ACCOUNT_LAMPORTS = 2039280; // ~0.00203928 SOL

/**
 * Fetch all empty (zero-balance) token accounts for a wallet
 * These can be closed to reclaim rent (~0.002 SOL each)
 * Includes both classic SPL and Token-2022 accounts
 *
 * @param connection - Solana RPC connection
 * @param owner - Wallet public key
 * @returns Array of empty token accounts that can be closed
 */
export async function fetchEmptyTokenAccounts(
  connection: Connection,
  owner: PublicKey
): Promise<EmptyTokenAccount[]> {
  const emptyAccounts: EmptyTokenAccount[] = [];

  // Query both Token Program and Token-2022 for empty accounts
  for (const programId of [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID]) {
    try {
      const accounts = await connection.getParsedTokenAccountsByOwner(owner, {
        programId,
      });

      const isToken2022 = programId.equals(TOKEN_2022_PROGRAM_ID);

      for (const { pubkey, account } of accounts.value) {
        const parsed = account.data.parsed.info;
        const mintAddress = parsed.mint as string;

        // Skip native SOL (wrapped SOL)
        if (mintAddress === NATIVE_SOL_MINT) continue;

        // Only include zero balance accounts
        const amount = BigInt(parsed.tokenAmount.amount);
        if (amount !== 0n) continue;

        // Check if we already have this account (avoid duplicates)
        if (emptyAccounts.some((a) => a.pubkey.equals(pubkey))) continue;

        emptyAccounts.push({
          pubkey,
          mint: new PublicKey(mintAddress),
          owner,
          programId,
          isToken2022,
          rentLamports: RENT_PER_ACCOUNT_LAMPORTS,
        });
      }
    } catch (error) {
      console.error("[tokens] Error fetching empty accounts:", error);
    }
  }

  // Enrich with metadata for display purposes
  if (emptyAccounts.length > 0) {
    const mintAddresses = emptyAccounts.map((a) => a.mint.toBase58());
    const metadataMap = await enrichTokensWithMetadata(connection, mintAddresses);

    for (const account of emptyAccounts) {
      const metadata = metadataMap.get(account.mint.toBase58());
      if (metadata) {
        account.symbol = metadata.symbol;
        account.name = metadata.name;
        account.logoURI = metadata.logoURI;
      }
    }
  }

  return emptyAccounts;
}
